import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  KeyOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagOutlined,
  SettingOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { AnyService, DatabaseService } from '../../types/service.type';
import { ServiceStatusBadge, ServiceEventsList, DetailPageHeader, ConfirmationModal, HeaderActionButton, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { getEngineDisplay } from '../../constants/databaseEngine';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';

// Helpers para mapear eventos del API
const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  const levelUpper = level.toUpperCase();
  switch (levelUpper) {
    case 'SUCCESS': return 'success';
    case 'ERROR': return 'error';
    case 'WARNING': return 'warning';
    default: return 'info';
  }
};

const mapStackStatusToEventType = (stackStatus?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!stackStatus) return 'info';
  const status = stackStatus.toUpperCase();
  if (status.includes('COMPLETE') && !status.includes('FAILED')) return 'success';
  if (status.includes('FAILED') || status.includes('ERROR') || status.includes('ROLLBACK')) return 'error';
  return 'info';
};

const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] => {
  return events.map((event, index) => {
    let timestamp: Date;
    try {
      if (!event.createdAt || typeof event.createdAt !== 'string') {
        timestamp = new Date();
      } else {
        timestamp = new Date(event.createdAt);
        if (isNaN(timestamp.getTime())) timestamp = new Date();
      }
    } catch {
      timestamp = new Date();
    }
    const eventType = event.level ? mapLevelToEventType(event.level) : mapStackStatusToEventType(event.stackStatus);
    const message = event.message || 'No message';
    const isKubiyEvent = event.source?.toLowerCase() === 'kubiy';
    const messageLower = message.toLowerCase();
    const hasDeletionKeywords =
      messageLower.includes('deleted') ||
      messageLower.includes('eliminado') ||
      messageLower.includes('service deleted') ||
      messageLower.includes('servicio eliminado');
    const stackStatusLower = event.stackStatus?.toLowerCase() || '';
    const levelUpper = event.level?.toUpperCase() || '';
    const hasDeleteCompleteStatus =
      stackStatusLower.includes('delete_complete') ||
      stackStatusLower.includes('delete_completed') ||
      stackStatusLower.includes('deleted');
    const isSuccessfulDeletion = levelUpper === 'SUCCESS' && hasDeletionKeywords;
    const isDeletionEvent =
      (isKubiyEvent && hasDeletionKeywords) || hasDeleteCompleteStatus || isSuccessfulDeletion;
    return {
      id: event.SK || `event-${index}-${event.createdAt}`,
      timestamp,
      type: eventType,
      message,
      details: event.description,
      source: event.source,
      isKubiyEvent,
      isDeletionEvent,
    };
  });
};

// Parsear IPs permitidas desde el API (securityRules.allowedCidrs, allowedCidrs, etc.)
const parseAllowedIPsFromApi = (data: any): string[] => {
  const normalizeCidrs = (cidrs: unknown[]): string[] =>
    cidrs.map((ip) => String(ip).trim()).filter(Boolean);

  if (Array.isArray(data.securityRules?.allowedCidrs)) {
    return normalizeCidrs(data.securityRules.allowedCidrs);
  }
  if (Array.isArray(data.allowedCidrs)) {
    return normalizeCidrs(data.allowedCidrs);
  }
  if (Array.isArray(data.allowedIPs)) {
    return normalizeCidrs(data.allowedIPs);
  }
  if (typeof data.allowedIPs === 'string' && data.allowedIPs.trim()) {
    return data.allowedIPs.split(/[,;\s]+/).map((ip: string) => ip.trim()).filter(Boolean);
  }
  if (data.metadata?.allowedIPs && Array.isArray(data.metadata.allowedIPs)) {
    return normalizeCidrs(data.metadata.allowedIPs);
  }
  if (data.metadata?.allowedCidrs && Array.isArray(data.metadata.allowedCidrs)) {
    return normalizeCidrs(data.metadata.allowedCidrs);
  }
  // Fallback: extraer CIDRs de ingress rules si existen
  const sr = data.securityRules;
  if (sr?.ingress && Array.isArray(sr.ingress)) {
    const cidrs = sr.ingress.map((r: any) => r.cidr).filter((c: string) => c && String(c).trim());
    return Array.from(new Set(cidrs));
  }
  return [];
};

interface DbAllowedServiceEntry {
  serviceId: string;
  tenantId?: string;
  serviceName?: string;
  serviceType?: string;
}

const parseAllowedServicesFromApi = (data: any): DbAllowedServiceEntry[] => {
  const sources = [
    data.securityRulesService,
    data.allowedServices,
    data.allowedSources,
    data.metadata?.allowedServices,
    data.metadata?.securityRulesService,
  ];

  for (const src of sources) {
    if (Array.isArray(src)) {
      const mapped = src
        .map((s: any) => {
          if (typeof s === 'string') {
            const serviceId = s.trim();
            return serviceId ? { serviceId } : null;
          }
          const serviceId = s?.serviceId || s?.service_id || s?.source || s?.id;
          if (!serviceId) return null;
          const tenantId = s?.sourceTenant || s?.tenantId || s?.tenant_id;
          return {
            serviceId: String(serviceId).trim(),
            tenantId: tenantId ? String(tenantId).trim() : undefined,
            serviceName: s?.serviceName || s?.name,
            serviceType: s?.serviceType || s?.type,
          };
        })
        .filter(Boolean) as DbAllowedServiceEntry[];

      if (mapped.length > 0) {
        return mapped;
      }
    }
  }
  return [];
};

const getServiceTypeOrCategory = (service: AnyService | Record<string, unknown>): string =>
  String(service.type || (service as any).category || '').trim();

const isVmServiceType = (type: string | undefined): boolean => {
  if (!type) return false;
  const upper = type.toUpperCase();
  return upper === 'VM' || upper === 'COMPUTE' || upper === 'VIRTUAL-MACHINE';
};

const isAppDeployServiceType = (type: string | undefined): boolean => {
  if (!type) return false;
  const upper = type.toUpperCase();
  return (
    upper === 'APP_DEPLOY' ||
    upper === 'APP_DEPLOY_STATIC' ||
    upper === 'APP_DEPLOY_SERVER' ||
    upper.startsWith('APP_DEPLOY') ||
    type.toLowerCase().startsWith('app_deploy')
  );
};

const isServerAppDeployService = (service: AnyService | Record<string, unknown>): boolean => {
  const typeOrCategory = getServiceTypeOrCategory(service);
  if (!isAppDeployServiceType(typeOrCategory)) return false;

  const upper = typeOrCategory.toUpperCase();
  if (upper === 'APP_DEPLOY_STATIC') return false;

  const appType = String((service as any).metadata?.appType || (service as any).appType || '')
    .toLowerCase()
    .trim();
  if (appType === 'static') return false;
  if (appType === 'server') return true;

  return upper === 'APP_DEPLOY_SERVER';
};

const isEligibleConnectableService = (service: AnyService, currentDbId?: string): boolean => {
  if (service.id === currentDbId) return false;
  const typeOrCategory = getServiceTypeOrCategory(service);
  if (isVmServiceType(typeOrCategory)) return true;
  return isServerAppDeployService(service);
};

const serviceEntryKey = (serviceId: string, tenantId?: string) =>
  tenantId ? `${tenantId}::${serviceId}` : serviceId;

const DB_REGION_NAMES: Record<string, string> = {
  'us-east-1':      'US East (N. Virginia)',
  'us-west-2':      'US West (Oregon)',
  'eu-west-1':      'EU (Irlanda)',
  'ap-southeast-1': 'Asia Pacífico (Singapur)',
};

const EVENTS_PER_PAGE = 10;

const DatabaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success, error: showError } = useNotificationContext();
  const [service, setService] = useState<DatabaseService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [credentials, setCredentials] = useState<{ username?: string; password?: string } | null>(null);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);
  const [activeTab, setActiveTab] = useState<'events' | 'security' | 'monitoring'>('events');
  const [allowedIPs, setAllowedIPs] = useState<string[]>([]);
  const [newIP, setNewIP] = useState('');
  const [isSavingAllowedIPs, setIsSavingAllowedIPs] = useState(false);
  const [isLoadingMyIP, setIsLoadingMyIP] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);

  const [allowedServices, setAllowedServices] = useState<DbAllowedServiceEntry[]>([]);
  const [addServiceMode, setAddServiceMode] = useState<'account' | 'manual'>('account');
  const [accountServices, setAccountServices] = useState<AnyService[]>([]);
  const [loadingAccountServices, setLoadingAccountServices] = useState(false);
  const [selectedAccountServiceId, setSelectedAccountServiceId] = useState('');
  const [manualTenantId, setManualTenantId] = useState('');
  const [manualServiceId, setManualServiceId] = useState('');
  const [isAddingAllowedService, setIsAddingAllowedService] = useState(false);
  const [removingServiceKey, setRemovingServiceKey] = useState<string | null>(null);

  useEffect(() => {
    const fetchService = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data: any = await servicesService.getById(id);
        if ((data.type || data.category || '').toUpperCase() === 'DATABASE') {
          setService(data as DatabaseService);
          setAllowedIPs(parseAllowedIPsFromApi(data));
          setAllowedServices(parseAllowedServicesFromApi(data));
        } else {
          setError('Service is not a database');
        }
      } catch (err: any) {
        setError(err?.message || 'Error loading database');
        setService(null);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'security' || addServiceMode !== 'account') return;

    const fetchAccountServices = async () => {
      setLoadingAccountServices(true);
      try {
        const [deployResponse, vmResponse] = await Promise.all([
          servicesService.getByCategory('APP_DEPLOY', { limit: 200 }),
          servicesService.getByCategory('VM', { limit: 200 }),
        ]);
        const merged = [...(deployResponse.services || []), ...(vmResponse.services || [])];
        const uniqueById = new Map(merged.map((s) => [s.id, s]));
        const eligible = Array.from(uniqueById.values()).filter((s) =>
          isEligibleConnectableService(s, id)
        );
        setAccountServices(eligible);
      } catch (err) {
        console.warn('Error loading account services:', err);
        setAccountServices([]);
      } finally {
        setLoadingAccountServices(false);
      }
    };

    fetchAccountServices();
  }, [activeTab, addServiceMode, id]);

  const getServiceTypeLabel = (type: string | undefined): string => {
    if (!type) return '—';
    if (isAppDeployServiceType(type)) {
      return t('databases.detail.securityRules.serviceTypeAppDeploy');
    }
    const normalized = type.toLowerCase();
    if (normalized === 'vm' || normalized === 'compute' || normalized === 'virtual-machine') {
      return t('databases.detail.securityRules.serviceTypeVm');
    }
    return type;
  };

  const handleAddAllowedService = async () => {
    if (!id) return;

    let sourceServiceId = '';
    let sourceTenant: string | undefined;

    if (addServiceMode === 'account') {
      if (!selectedAccountServiceId) return;
      const selected = accountServices.find((s) => s.id === selectedAccountServiceId);
      if (!selected) return;
      sourceServiceId = selected.id;
    } else {
      sourceTenant = manualTenantId.trim();
      sourceServiceId = manualServiceId.trim();
      if (!sourceTenant || !sourceServiceId) return;
    }

    if (
      allowedServices.some(
        (s) => serviceEntryKey(s.serviceId, s.tenantId) === serviceEntryKey(sourceServiceId, sourceTenant)
      )
    ) {
      showError(t('databases.detail.securityRules.serviceAlreadyExists'));
      return;
    }

    setIsAddingAllowedService(true);
    try {
      await servicesService.allowDbServiceSource(id, {
        source: sourceServiceId,
        ...(sourceTenant ? { sourceTenant } : {}),
      });
      success(t('databases.detail.securityRules.serviceAdded'));
      setSelectedAccountServiceId('');
      setManualTenantId('');
      setManualServiceId('');
      await refreshService();
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.securityRulesError'));
    } finally {
      setIsAddingAllowedService(false);
    }
  };

  const handleRemoveAllowedService = async (entry: DbAllowedServiceEntry) => {
    if (!id) return;

    const key = serviceEntryKey(entry.serviceId, entry.tenantId);
    setRemovingServiceKey(key);
    try {
      await servicesService.revokeDbServiceSource(id, {
        source: entry.serviceId,
        ...(entry.tenantId ? { sourceTenant: entry.tenantId } : {}),
      });
      success(t('databases.detail.securityRules.serviceRemoved'));
      await refreshService();
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.securityRulesError'));
    } finally {
      setRemovingServiceKey(null);
    }
  };

  const handleRefreshEvents = async () => {
    if (!id) return;
    setIsRefreshingEvents(true);
    try {
      const eventsData = await servicesService.getEvents(id);
      setEvents(mapServiceEventsToUI(eventsData));
      success(t('virtualMachines.detail.events.refreshed') || t('databases.detail.refresh'));
    } catch (err: any) {
      showError(err?.message || 'Error updating events');
    } finally {
      setIsRefreshingEvents(false);
    }
  };

  const getUserIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || '';
    } catch {
      return '';
    }
  };

  const handleAddMyIP = async () => {
    setIsLoadingMyIP(true);
    try {
      const userIP = await getUserIP();
      if (userIP) {
        const cidr = `${userIP}/32`;
        if (allowedIPs.includes(cidr) || allowedIPs.includes(userIP)) {
          showError(t('databases.detail.securityRules.ipAlreadyExists'));
          return;
        }
        setAllowedIPs((prev) => [...prev, cidr]);
      } else {
        showError(t('databases.detail.securityRules.ipError') || t('notifications.vm.ipError'));
      }
    } finally {
      setIsLoadingMyIP(false);
    }
  };

  const handleAddIP = () => {
    const ip = newIP.trim();
    if (!ip) return;
    if (allowedIPs.includes(ip)) {
      showError(t('databases.detail.securityRules.ipAlreadyExists') || 'Esta IP ya está en la lista');
      return;
    }
    setAllowedIPs((prev) => [...prev, ip]);
    setNewIP('');
  };

  const handleRemoveIP = (ip: string) => {
    setAllowedIPs((prev) => prev.filter((i) => i !== ip));
  };

  const refreshService = async () => {
    if (!id) return;
    try {
      const data: any = await servicesService.getById(id);
      if ((data.type || data.category || '').toUpperCase() === 'DATABASE') {
        setService(data as DatabaseService);
        setAllowedIPs(parseAllowedIPsFromApi(data));
        setAllowedServices(parseAllowedServicesFromApi(data));
      }
    } catch (err) {
      console.warn('Error refreshing service:', err);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await servicesService.delete(id);
      success(t('notifications.database.deleteSuccess') || 'Base de datos eliminada');
      navigate('/databases');
    } catch (err: any) {
      showError(err?.message || 'Error al eliminar la base de datos');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleStart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      await servicesService.dbStart(id);
      success(t('notifications.database.startSuccess'));
      await refreshService();
    } catch (err: any) {
      showError(err?.message || t('notifications.database.startError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      await servicesService.dbStop(id);
      success(t('notifications.database.stopSuccess'));
      await refreshService();
    } catch (err: any) {
      showError(err?.message || t('notifications.database.stopError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      await servicesService.dbRestart(id);
      success(t('notifications.database.restartSuccess'));
      await refreshService();
    } catch (err: any) {
      showError(err?.message || t('notifications.database.restartError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAllowedIPs = async () => {
    if (!id) return;
    setIsSavingAllowedIPs(true);
    try {
      // Normalizar: IPs sin /XX se envían como /32
      const allowedCidrs = allowedIPs.map((ip) => (ip.includes('/') ? ip : `${ip}/32`));
      await servicesService.updateDbAllowedIPs(id, allowedCidrs);
      success(t('notifications.vm.securityRulesSuccess') || t('databases.detail.securityRules.saved'));
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.securityRulesError'));
    } finally {
      setIsSavingAllowedIPs(false);
    }
  };

  const handleOpenCredentialsModal = () => {
    setCredentials(null);
    setPasswordVisible(false);
    setCredentialsModalOpen(true);
  };

  const handleFetchCredentials = async () => {
    if (!id) return;
    setLoadingPassword(true);
    try {
      const data = await servicesService.getDbMasterCredentials(id);
      setCredentials({
        username: data.username,
        password: data.password,
      });
      setPasswordVisible(false);
    } catch (err: any) {
      showError(err?.message || t('databases.detail.credentialsError'));
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseModal = () => {
    setCredentialsModalOpen(false);
    setCredentials(null);
    setPasswordVisible(false);
    setLoadingPassword(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingOutlined className="text-4xl text-gray-400" />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error || 'Database not found'}</p>
          <button
            onClick={() => navigate('/databases')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const statusUpper = (service.status || '').toUpperCase();
  const isRunning = statusUpper === 'RUNNING';
  const isStopped = statusUpper === 'STOPPED';

  const InfoRow = ({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
      {icon && <span className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0">{icon}</span>}
      <span className="text-sm text-gray-500 dark:text-gray-400 w-36 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">{value}</span>
    </div>
  );

  const CopyCode = ({ text, title }: { text: string; title: string }) => (
    <span className="flex items-center gap-1.5">
      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">{text}</code>
      <button
        onClick={() => navigator.clipboard.writeText(text)}
        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        title={title}
      >
        <CopyOutlined className="text-xs" />
      </button>
    </span>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel={t('common.back')}
        onBack={() => navigate('/databases')}
        title={service.name}
        subtitle={`${service.engine} ${service.version} • ${t('databases.detail.created')} ${getServiceAge(service.createdAt)}`}
        actions={
          <>
            {isStopped && (
              <HeaderActionButton variant="success" icon={<PlayCircleOutlined />} onClick={handleStart} disabled={isProcessing}>
                {t('virtualMachines.detail.start')}
              </HeaderActionButton>
            )}
            {isRunning && (
              <>
                <HeaderActionButton variant="secondary" icon={<PauseCircleOutlined />} onClick={() => setShowStopModal(true)} disabled={isProcessing}>
                  {t('virtualMachines.detail.stop')}
                </HeaderActionButton>
                <HeaderActionButton variant="secondary" icon={<SyncOutlined />} onClick={handleRestart} disabled={isProcessing}>
                  {t('virtualMachines.detail.restart')}
                </HeaderActionButton>
              </>
            )}
            <HeaderActionMenu
              items={[
                {
                  key: 'refresh',
                  label: t('common.refresh'),
                  icon: <ReloadOutlined />,
                  onClick: () => window.location.reload(),
                },
                {
                  key: 'credentials',
                  label: t('databases.detail.credentials'),
                  icon: <KeyOutlined />,
                  onClick: handleOpenCredentialsModal,
                },
                {
                  key: 'delete',
                  label: t('common.delete'),
                  icon: <DeleteOutlined />,
                  onClick: () => setShowDeleteModal(true),
                  danger: true,
                },
              ]}
            />
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* Resumen / Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('databases.detail.overview')}</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-6 space-y-6">
              {/* Endpoint de conexión */}
              {service.publicDnsNameKubiy && (
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Endpoint de conexión</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">{service.publicDnsNameKubiy}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(service.publicDnsNameKubiy!)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0 transition-colors"
                      title={t('common.copy')}
                    >
                      <CopyOutlined />
                    </button>
                  </div>
                  <div className="flex items-center gap-4 mt-2.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Puerto: <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{service.port}</span>
                    </span>
                    {service.username && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Usuario: <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{service.username}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Endpoint privado (misma VPC · SG-a-SG) */}
              {(service.sqlPrivateEndpoint || service.privateIp) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-2">Endpoint privado (misma VPC)</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">{service.sqlPrivateEndpoint || `${service.privateIp},${service.port}`}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(service.sqlPrivateEndpoint || `${service.privateIp},${service.port}`)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0 transition-colors"
                      title={t('common.copy')}
                    >
                      <CopyOutlined />
                    </button>
                  </div>
                  <p className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-2">
                    Úsalo desde apps en la misma VPC (regla SG-a-SG): la conexión no sale a internet. El endpoint público de arriba sí sale a internet.
                  </p>
                </div>
              )}

              {/* Identidad */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Identidad</h4>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  <InfoRow icon={<DatabaseOutlined />} label={t('databases.detail.serviceId')} value={<CopyCode text={service.id} title={t('common.copy')} />} />
                  <InfoRow icon={<DatabaseOutlined />} label={t('databases.detail.engine')} value={`${service.engine} ${service.version}`} />
                  <InfoRow icon={<EnvironmentOutlined />} label={t('databases.detail.region')} value={DB_REGION_NAMES[service.region || ''] ?? service.region ?? '—'} />
                  <InfoRow
                    icon={<CalendarOutlined />}
                    label={t('databases.detail.created')}
                    value={
                      <span>
                        {new Date(service.createdAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({getServiceAge(service.createdAt)})</span>
                      </span>
                    }
                  />
                </div>
              </div>

              {/* Configuración */}
              {(() => {
                const svcAny = service as any;
                const volGB: number = svcAny.volumeGB || 0;
                const volType: string | undefined = svcAny.volumeType;
                const backupDays: number | undefined = svcAny.backupRetentionDays;
                const multiAz: boolean | undefined = svcAny.multiAz;
                const az: string | undefined = svcAny.availabilityZone;
                const hasConfig = service.instanceType || (!service.publicDnsNameKubiy && service.port)
                  || volGB > 0 || backupDays !== undefined || multiAz !== undefined || az;
                if (!hasConfig) return null;
                return (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Configuración</h4>
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {service.instanceType && (
                        <InfoRow icon={<SettingOutlined />} label={t('databases.detail.instanceType')} value={service.instanceType} />
                      )}
                      {!service.publicDnsNameKubiy && service.port && (
                        <InfoRow icon={<InfoCircleOutlined />} label={t('databases.detail.port')} value={String(service.port)} />
                      )}
                      {volGB > 0 && (
                        <InfoRow
                          icon={<DatabaseOutlined />}
                          label="Almacenamiento"
                          value={<span>{volGB} GB{volType && <span className="ml-1.5 text-xs text-gray-400 font-normal">({volType})</span>}</span>}
                        />
                      )}
                      {backupDays !== undefined && (
                        <InfoRow
                          icon={<CalendarOutlined />}
                          label="Retención backups"
                          value={backupDays === 0
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">Desactivado</span>
                            : `${backupDays} día${backupDays !== 1 ? 's' : ''}`
                          }
                        />
                      )}
                      {multiAz !== undefined && (
                        <InfoRow
                          icon={<EnvironmentOutlined />}
                          label="Multi-AZ"
                          value={multiAz
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">Sí</span>
                            : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">No</span>
                          }
                        />
                      )}
                      {az && (
                        <InfoRow icon={<EnvironmentOutlined />} label="Zona de disponibilidad" value={az} />
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Subscripción */}
              {(() => {
                const sub = (service as any).subscription as {
                  id?: string; status?: string; billingInterval?: string;
                  amount?: number; currency?: string; currentPeriodEnd?: string;
                  cancelAtPeriodEnd?: boolean;
                } | null;
                if (!sub) return null;

                const subStatusConfig: Record<string, { label: string; className: string }> = {
                  ACTIVE:    { label: 'Activa',         className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
                  PAST_DUE:  { label: 'Pago pendiente', className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
                  CANCELLED: { label: 'Cancelada',      className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                  SUSPENDED: { label: 'Suspendida',     className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                };
                const sc = subStatusConfig[sub.status || ''] ?? { label: sub.status || '—', className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' };
                const intervalLabel = sub.billingInterval === 'YEAR' ? 'anual' : 'mensual';
                const amountFmt = sub.amount != null
                  ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: (sub.currency || 'MXN').toUpperCase() }).format(sub.amount / 100)
                  : null;
                const nextCharge = sub.currentPeriodEnd
                  ? new Date(sub.currentPeriodEnd).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                  : null;

                return (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Subscripción</h4>
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      <InfoRow icon={<TagOutlined />} label="Estado" value={<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.className}`}>{sc.label}</span>} />
                      {amountFmt && <InfoRow icon={<CalendarOutlined />} label="Cobro" value={`${amountFmt} ${intervalLabel}`} />}
                      {nextCharge && <InfoRow icon={<CalendarOutlined />} label={sub.cancelAtPeriodEnd ? 'Cancela el' : 'Próximo cobro'} value={nextCharge} />}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Tabs: Resumen, Eventos, Reglas de seguridad */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex gap-1 px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('events')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'events'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('databases.detail.events')}
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'security'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('databases.detail.security')}
                </button>
                <button
                  onClick={() => setActiveTab('monitoring')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'monitoring'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Monitoring
                </button>
              </div>
            </div>
            <div className="p-6">
              {activeTab === 'events' && (
                <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />
              )}

              {activeTab === 'monitoring' && service && (
                <MonitoringTab service={service as any} />
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('databases.detail.securityRules.allowedIPsTitle')}</h3>
                    <p className="text-sm text-gray-500 mt-1">{t('databases.detail.securityRules.allowedIPsSubtitle')}</p>
                  </div>

                  {/* Agregar IP */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={newIP}
                      onChange={(e) => setNewIP(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddIP()}
                      placeholder={t('databases.detail.securityRules.ipPlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddMyIP}
                        disabled={isLoadingMyIP}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                        title={t('databases.detail.securityRules.addMyIP')}
                      >
                        {isLoadingMyIP ? (
                          <LoadingOutlined spin />
                        ) : (
                          <>
                            <PlusOutlined />
                            {t('databases.detail.securityRules.addMyIP')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleAddIP}
                        disabled={!newIP.trim()}
                        className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <PlusOutlined />
                        {t('databases.detail.securityRules.addIP')}
                      </button>
                    </div>
                  </div>

                  {/* Lista de IPs */}
                  {allowedIPs.length > 0 ? (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {allowedIPs.map((ip) => (
                          <li
                            key={ip}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <code className="text-sm font-mono text-gray-900 dark:text-white">{ip}</code>
                            <button
                              onClick={() => handleRemoveIP(ip)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title={t('common.delete')}
                            >
                              <DeleteOutlined />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-500">{t('databases.detail.securityRules.noAllowedIPs')}</p>
                      <p className="text-xs text-gray-400 mt-1">{t('databases.detail.securityRules.noAllowedIPsDesc')}</p>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSaveAllowedIPs}
                      disabled={isSavingAllowedIPs}
                      className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                    >
                      {isSavingAllowedIPs ? t('databases.detail.securityRules.saving') : t('databases.detail.securityRules.save')}
                    </button>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('databases.detail.securityRules.allowedServicesTitle')}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('databases.detail.securityRules.allowedServicesSubtitle')}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAddServiceMode('account')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          addServiceMode === 'account'
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t('databases.detail.securityRules.addServiceModeAccount')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddServiceMode('manual')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          addServiceMode === 'manual'
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t('databases.detail.securityRules.addServiceModeManual')}
                      </button>
                    </div>

                    {addServiceMode === 'account' ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          value={selectedAccountServiceId}
                          onChange={(e) => setSelectedAccountServiceId(e.target.value)}
                          disabled={loadingAccountServices}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          <option value="">
                            {loadingAccountServices
                              ? t('databases.detail.securityRules.loadingServices')
                              : t('databases.detail.securityRules.selectServicePlaceholder')}
                          </option>
                          {accountServices.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} ({getServiceTypeLabel(getServiceTypeOrCategory(s))})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddAllowedService}
                          disabled={!selectedAccountServiceId || loadingAccountServices || isAddingAllowedService}
                          className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isAddingAllowedService ? <LoadingOutlined spin /> : <PlusOutlined />}
                          {t('databases.detail.securityRules.addService')}
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={manualTenantId}
                          onChange={(e) => setManualTenantId(e.target.value)}
                          placeholder={t('databases.detail.securityRules.tenantIdPlaceholder')}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={manualServiceId}
                          onChange={(e) => setManualServiceId(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddAllowedService()}
                          placeholder={t('databases.detail.securityRules.serviceIdPlaceholder')}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                        <div className="sm:col-span-2 flex justify-end">
                          <button
                            type="button"
                            onClick={handleAddAllowedService}
                            disabled={!manualTenantId.trim() || !manualServiceId.trim() || isAddingAllowedService}
                            className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isAddingAllowedService ? <LoadingOutlined spin /> : <PlusOutlined />}
                            {t('databases.detail.securityRules.addService')}
                          </button>
                        </div>
                      </div>
                    )}

                    {!loadingAccountServices && addServiceMode === 'account' && accountServices.length === 0 && (
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        {t('databases.detail.securityRules.noEligibleServices')}
                      </p>
                    )}

                    {allowedServices.length > 0 ? (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                          {allowedServices.map((entry) => (
                            <li
                              key={serviceEntryKey(entry.serviceId, entry.tenantId)}
                              className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <div className="min-w-0 flex-1">
                                {entry.serviceName && (
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {entry.serviceName}
                                    {entry.serviceType && (
                                      <span className="ml-2 text-xs font-normal text-gray-500">
                                        ({getServiceTypeLabel(entry.serviceType)})
                                      </span>
                                    )}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                  {entry.tenantId && (
                                    <span className="text-xs text-gray-500">
                                      {t('databases.detail.securityRules.tenantId')}:
                                      <code className="ml-1 font-mono text-gray-700 dark:text-gray-300">
                                        {entry.tenantId.replace(/^tenant-/, '')}
                                      </code>
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-500">
                                    {t('databases.detail.securityRules.serviceIdField')}:
                                    <code className="ml-1 font-mono text-gray-700 dark:text-gray-300">{entry.serviceId}</code>
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveAllowedService(entry)}
                                disabled={removingServiceKey === serviceEntryKey(entry.serviceId, entry.tenantId)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded shrink-0 disabled:opacity-50"
                                title={t('common.delete')}
                              >
                                {removingServiceKey === serviceEntryKey(entry.serviceId, entry.tenantId) ? (
                                  <LoadingOutlined spin />
                                ) : (
                                  <DeleteOutlined />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                        <p className="text-sm text-gray-500">{t('databases.detail.securityRules.noAllowedServices')}</p>
                        <p className="text-xs text-gray-400 mt-1">{t('databases.detail.securityRules.noAllowedServicesDesc')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          {/* Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.status')}</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.status')}</span>
                <ServiceStatusBadge status={service.status} size="sm" showLabel />
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.health')}</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      service.health?.toUpperCase() === 'HEALTHY'
                        ? 'bg-green-500'
                        : service.health?.toUpperCase() === 'DEGRADED'
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{service.health || 'unknown'}</span>
                </div>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.lastUpdated')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{getServiceAge(service.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('databases.detail.created')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{getServiceAge(service.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Product Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('orders.detail.product')}</h3>
            </div>
            <div className="p-6 space-y-3">
              {(() => {
                const { image } = getEngineDisplay(service.engine || '');
                return image ? (
                  <div className="flex justify-center mb-4">
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center bg-transparent">
                      <img src={image} alt={service.engine} className="w-20 h-20 object-contain" />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center mb-4">
                    <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                      <DatabaseOutlined className="text-4xl text-white" />
                    </div>
                  </div>
                );
              })()}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">{t('databases.detail.engine')}</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{service.engine} {service.version}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions — mismos estilos que ClickHouse detail */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('databases.detail.quickActions')}</h3>
            </div>
            <div className="p-6 space-y-3">
              {isRunning && (
                <>
                  <button
                    type="button"
                    onClick={handleStop}
                    disabled={isProcessing}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <PauseCircleOutlined />
                    {t('virtualMachines.detail.stop')}
                  </button>
                  <button
                    type="button"
                    onClick={handleRestart}
                    disabled={isProcessing}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <SyncOutlined />
                    {t('virtualMachines.detail.restart')}
                  </button>
                </>
              )}
              {isStopped && (
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={isProcessing}
                  className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <PlayCircleOutlined />
                  {t('virtualMachines.detail.start')}
                </button>
              )}
              {isProcessing && (
                <div className="flex items-center justify-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 py-2">
                  <LoadingOutlined spin />
                  <span>{t('databases.detail.processing')}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleOpenCredentialsModal}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
              >
                <KeyOutlined />
                {t('databases.detail.credentials')}
              </button>
            </div>
          </div>

          {/* Documentación */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
            <a
              href="https://docs.kubiy.com/databases"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <span>Ver documentación</span>
              <GlobalOutlined className="text-xs" />
            </a>
          </div>
        </div>
      </div>

      {/* Credentials Modal */}
      {credentialsModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={handleCloseModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('databases.detail.masterCredentials')}</h2>

              {!credentials ? (
                <div className="py-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('databases.detail.fetchCredentialsHint')}</p>
                  <button
                    onClick={handleFetchCredentials}
                    disabled={loadingPassword}
                    className="flex items-center gap-2 px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                  >
                    {loadingPassword ? (
                      <LoadingOutlined className="animate-spin" />
                    ) : (
                      <EyeOutlined />
                    )}
                    {t('databases.detail.recoverCredentials')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">{t('databases.detail.username')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded font-mono">{credentials.username || '—'}</code>
                      <button
                        onClick={() => credentials.username && handleCopy(credentials.username)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title={t('common.copy')}
                      >
                        <CopyOutlined />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">{t('databases.detail.password')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded font-mono break-all">
                        {passwordVisible ? credentials.password : '••••••••'}
                      </code>
                      <button
                        onClick={() => credentials.password && handleCopy(credentials.password)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title={t('common.copy')}
                      >
                        {copied ? <span className="text-xs text-green-600">{t('common.copied')}</span> : <CopyOutlined />}
                      </button>
                      <button
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        title={passwordVisible ? t('databases.detail.hidePassword') : t('databases.detail.viewPassword')}
                      >
                        {passwordVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('common.delete') + ' ' + (service?.name || '')}
        message={`¿Seguro que quieres eliminar "${service?.name}"? Esto borra la base de datos RDS y sus respaldos de forma permanente.`}
        confirmText={t('common.delete')}
        loading={isDeleting}
        variant="danger"
      />

      <ConfirmationModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onConfirm={() => { setShowStopModal(false); handleStop(); }}
        title={t('virtualMachines.detail.stop') + ' ' + (service?.name || '')}
        message={'Detener es temporal: AWS reactiva automáticamente una base de datos RDS detenida a los 7 días (no se puede mantener apagada más tiempo). Mientras esté detenida no cobra la instancia, pero al reactivarse volverá a facturar. ¿Detener de todos modos?'}
        confirmText={t('virtualMachines.detail.stop')}
        loading={isProcessing}
        variant="default"
      />
    </div>
  );
};

export default DatabaseDetail;
